/* ============================================================
LEGAL LINKS — MODAL-BASED (NO NAVIGATION)
Goal:
- Show Privacy Policy + Terms in a modal (no white page issue)
- Works for email sign-in AND Google sign-in
- RTL/LTR aware
- Clean minimal design
============================================================ */

import React, { useEffect, useMemo } from 'react';

/* --------------------------
1) TRANSLATIONS
--------------------------- */

export const authLegalText = {
  ar: {
    agreeText: "بمتابعة التسجيل فإنك توافق على",
    privacy: "سياسة الخصوصية",
    and: "و",
    terms: "شروط الاستخدام",
  },
  en: {
    agreeText: "By continuing, you agree to our",
    privacy: "Privacy Policy",
    and: "and",
    terms: "Terms of Use",
  },
};

/* --------------------------
2) LEGAL FOOTER COMPONENT
--------------------------- */

export function AuthLegalFooter({ locale = "ar", onOpenPrivacy, onOpenTerms }) {
  const t = authLegalText[locale] || authLegalText.en;
  const isRTL = locale === "ar";

  return (
    <div
      className="auth-legal-footer"
      dir={isRTL ? "rtl" : "ltr"}
      data-testid="auth-legal-footer"
    >
      <p>
        {t.agreeText}{" "}
        <button 
          type="button" 
          onClick={onOpenPrivacy} 
          className="legal-link-btn"
        >
          {t.privacy}
        </button>{" "}
        {t.and}{" "}
        <button 
          type="button" 
          onClick={onOpenTerms} 
          className="legal-link-btn"
        >
          {t.terms}
        </button>
      </p>

      <style>{`
        .auth-legal-footer {
          margin-top: 20px;
          text-align: center;
          font-size: 13px;
          color: #666;
          line-height: 1.6;
          padding: 10px 20px 0;
        }

        .auth-legal-footer p {
          margin: 0;
        }

        .legal-link-btn {
          border: none;
          background: transparent;
          color: #ff8a00;
          font-weight: 600;
          cursor: pointer;
          padding: 0;
          text-decoration: underline;
          transition: all 0.2s ease;
          font-size: inherit;
        }

        .legal-link-btn:hover {
          color: #e67a00;
        }
      `}</style>
    </div>
  );
}

/* --------------------------
3) LEGAL MODAL COMPONENT
--------------------------- */

export function LegalModal({ open, onClose, type = "terms", locale = "en" }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isAr = locale === "ar";
  const isTerms = type === "terms";
  const title = isTerms
    ? isAr
      ? "شروط الاستخدام"
      : "Terms of Use"
    : isAr
    ? "سياسة الخصوصية"
    : "Privacy Policy";

  return (
    <div 
      className="legal-modal-backdrop" 
      onClick={onClose} 
      role="dialog" 
      aria-modal="true"
    >
      <div 
        className="legal-modal-sheet" 
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          className="legal-modal-header" 
          style={{ direction: isAr ? "rtl" : "ltr" }}
        >
          <button 
            onClick={onClose} 
            className="legal-modal-close" 
            aria-label="Close"
          >
            ✕
          </button>
          <div className="legal-modal-title">{title}</div>
          <div style={{ width: 40 }} />
        </div>

        <div 
          className="legal-modal-body" 
          style={{ direction: isAr ? "rtl" : "ltr" }}
        >
          {isTerms ? <TermsContent lang={locale} /> : <PrivacyContent lang={locale} />}
        </div>

        <div className="legal-modal-footer">
          <button onClick={onClose} className="legal-modal-ok-btn" data-testid="legal-modal-agree">
            {isAr ? "أوافق" : "Agree"}
          </button>
        </div>
      </div>

      <style>{`
        .legal-modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: rgba(0, 0, 0, 0.55);
          display: flex;
          align-items: flex-end;
          justify-content: center;
          padding: 12px;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .legal-modal-sheet {
          width: 100%;
          max-width: 560px;
          max-height: 85vh;
          background: #fff;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
          animation: slideUp 0.3s ease;
        }

        html.dark .legal-modal-sheet {
          background: #1f2937;
        }

        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .legal-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 12px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        }

        html.dark .legal-modal-header {
          border-bottom-color: rgba(255, 255, 255, 0.1);
        }

        .legal-modal-close {
          width: 40px;
          height: 40px;
          border-radius: 999px;
          border: 1px solid rgba(0, 0, 0, 0.1);
          background: #fff;
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        html.dark .legal-modal-close {
          background: #374151;
          border-color: rgba(255, 255, 255, 0.1);
          color: #fff;
        }

        .legal-modal-close:hover {
          background: #f5f5f5;
          transform: scale(1.05);
        }

        html.dark .legal-modal-close:hover {
          background: #4b5563;
        }

        .legal-modal-title {
          font-weight: 900;
          font-size: 16px;
          color: #111;
        }

        html.dark .legal-modal-title {
          color: #fff;
        }

        .legal-modal-body {
          padding: 14px;
          overflow-y: auto;
          max-height: 60vh;
        }

        .legal-modal-footer {
          padding: 12px;
          border-top: 1px solid rgba(0, 0, 0, 0.08);
        }

        html.dark .legal-modal-footer {
          border-top-color: rgba(255, 255, 255, 0.1);
        }

        .legal-modal-ok-btn {
          width: 100%;
          border: 0;
          background: linear-gradient(135deg, #ff8a00, #ffb347);
          color: #fff;
          padding: 12px 14px;
          font-weight: 900;
          border-radius: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .legal-modal-ok-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(255, 138, 0, 0.3);
        }

        .legal-modal-ok-btn:active {
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}

/* --------------------------
4) TERMS CONTENT (iter306 — centralised)
--------------------------- */

import { TERMS_SHORT, PRIVACY_SHORT, LAST_UPDATED_EN, LAST_UPDATED_AR } from '../data/legalContent';

function TermsContent({ lang }) {
  const doc = TERMS_SHORT[lang === 'ar' ? 'ar' : 'en'];
  const isAr = lang === 'ar';
  const updated = isAr ? LAST_UPDATED_AR : LAST_UPDATED_EN;
  return (
    <div className="legal-content" data-testid="legal-modal-terms">
      <div className="legal-content-updated">
        {isAr ? `آخر تحديث: ${updated}` : `Last updated: ${updated}`}
      </div>
      {doc.sections.map((s, i) => (
        <React.Fragment key={i}>
          <h3>{s.h}</h3>
          <p>{s.p}</p>
        </React.Fragment>
      ))}
      <style>{`
        .legal-content { line-height: 1.7; font-size: 14px; color: #333; }
        html.dark .legal-content { color: #e5e5e5; }
        .legal-content-updated {
          font-size: 12px; color: #666; margin-bottom: 12px;
        }
        html.dark .legal-content-updated { color: #9ca3af; }
        .legal-content h3 {
          font-size: 15px; font-weight: 700; margin: 16px 0 8px; color: #111;
        }
        html.dark .legal-content h3 { color: #fff; }
        .legal-content h3:first-of-type { margin-top: 0; }
        .legal-content p { margin: 0 0 12px; }
      `}</style>
    </div>
  );
}

/* --------------------------
5) PRIVACY CONTENT (iter306 — centralised)
--------------------------- */

function PrivacyContent({ lang }) {
  const doc = PRIVACY_SHORT[lang === 'ar' ? 'ar' : 'en'];
  const isAr = lang === 'ar';
  const updated = isAr ? LAST_UPDATED_AR : LAST_UPDATED_EN;
  return (
    <div className="legal-content" data-testid="legal-modal-privacy">
      <div className="legal-content-updated">
        {isAr ? `آخر تحديث: ${updated}` : `Last updated: ${updated}`}
      </div>
      {doc.sections.map((s, i) => (
        <React.Fragment key={i}>
          <h3>{s.h}</h3>
          <p>{s.p}</p>
        </React.Fragment>
      ))}
      <style>{`
        .legal-content { line-height: 1.7; font-size: 14px; color: #333; }
        html.dark .legal-content { color: #e5e5e5; }
        .legal-content-updated {
          font-size: 12px; color: #666; margin-bottom: 12px;
        }
        html.dark .legal-content-updated { color: #9ca3af; }
        .legal-content h3 {
          font-size: 15px; font-weight: 700; margin: 16px 0 8px; color: #111;
        }
        html.dark .legal-content h3 { color: #fff; }
        .legal-content h3:first-of-type { margin-top: 0; }
        .legal-content p { margin: 0 0 12px; }
      `}</style>
    </div>
  );
}
